import React, { useState } from 'react';
import { Box, Typography, Paper, List, ListItem, ListItemText, Divider, TextField, Button, Stack, Alert, CircularProgress } from '@mui/material';
import { apiConfig } from '../config/api';

export default function ApiExplorerGuide() {
  const [vatInput, setVatInput] = useState('DE123456789');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleTestRequest = async () => {
    if (!vatInput.trim()) {
      setError('Enter a VAT number with country code.');
      return;
    }
    if (!apiKeyInput.trim()) {
      setError('Enter your API key (X-Api-Key).');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${apiConfig.baseURL}/api/vat/${vatInput.trim()}`, {
        method: 'GET',
        headers: {
          'X-Api-Key': apiKeyInput.trim(),
          'Accept': 'application/json',
        },
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        const message = payload?.message || payload?.error || `Request failed (${response.status})`;
        throw new Error(message);
      }

      setResult(payload);
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxWidth={700} mx="auto" mt={4}>
      <Typography variant="h4" gutterBottom>API Explorer Guide</Typography>
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>How to Use the VAT Validation API</Typography>
        <Typography gutterBottom>
          The VAT Validation API allows you to check the validity of VAT numbers for EU, UK, CH, and NO countries.
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Endpoint"
              secondary="/api/vat/{vatid} (GET)"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Authentication"
              secondary="Send your API key in header X-Api-Key"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Path Parameter"
              secondary="vatid: The VAT number to check, including country code (e.g., 'DE123456789', 'GB123456789', 'CH123456', 'NO123456789')"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Example Request"
              secondary="GET /api/vat/DE123456789"
            />
          </ListItem>
        </List>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Sample Response</Typography>
        <Paper variant="outlined" sx={{ p: 2, background: '#f7f7f7', fontFamily: 'monospace', fontSize: 14 }}>
          {`{
  "isValid": true,
  "name": "Sample Company Name",
  "address": "Sample Address, City, Country",
  "VAT": "DE123456789",
  "Country": "DE"
}`}
        </Paper>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Example cURL Requests</Typography>
        <Paper variant="outlined" sx={{ p: 2, background: '#f7f7f7', fontFamily: 'monospace', fontSize: 14, mb: 2 }}>
          {`# Simple request
curl -X GET "https://your-domain/api/vat/DE123456789" \\
  -H "X-Api-Key: YOUR_API_KEY_HERE"`}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, background: '#f7f7f7', fontFamily: 'monospace', fontSize: 14 }}>
          {`# Request with explicit accept header
curl -X GET "https://your-domain/api/vat/DE123456789" \\
  -H "X-Api-Key: YOUR_API_KEY_HERE" \\
  -H "Accept: application/json"`}
        </Paper>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Try It Here (Live Request)</Typography>
        <Paper variant="outlined" sx={{ p: 2, background: '#fafafa' }}>
          <Stack spacing={2}>
            <TextField
              label="VAT with country code"
              value={vatInput}
              onChange={(e) => setVatInput(e.target.value)}
              placeholder="e.g., DE123456789"
              fullWidth
            />
            <TextField
              label="API Key (X-Api-Key)"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              placeholder="Paste your API key"
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleTestRequest}
              disabled={loading}
            >
              {loading ? <CircularProgress size={18} /> : 'Send Test Request'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              Requests are sent to {apiConfig.baseURL}/api/vat/{vatInput || 'VAT'} with header X-Api-Key.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {result && (
              <Paper variant="outlined" sx={{ p: 2, background: '#f7f7f7', fontFamily: 'monospace', fontSize: 14 }}>
                <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(result, null, 2)}
                </Box>
              </Paper>
            )}
          </Stack>
        </Paper>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Supported Countries</Typography>
        <Typography>
          <b>EU:</b> AT, BE, BG, CY, CZ, DE, DK, EE, EL, ES, FI, FR, HR, HU, IE, IT, LT, LU, LV, MT, NL, PL, PT, RO, SE, SI, SK<br />
          <b>UK:</b> GB<br />
          <b>Switzerland:</b> CH<br />
          <b>Norway:</b> NO
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">
          For more details, see the API documentation or contact support.
        </Typography>
      </Paper>
    </Box>
  );
}
